import { useState, useEffect } from "react";
import { useAdminData, type AdminUser } from "./AdminDataContext";
import { useAdminAuth } from "./AdminAuthContext";
import { Plus, Pencil, Trash2, X, AlertTriangle, Shield, BookOpen, Settings, Database, HardDrive } from "lucide-react";
import { isSupabaseConnected, createAdminUser, updateAdminUser as updateAdminUserSb, deleteAdminUser as deleteAdminUserSb, fetchAdminUsers } from "../supabase";
import { toast } from "sonner";

const roleConfig: Record<AdminUser["role"], { label: string; color: string; icon: typeof Shield; desc: string }> = {
  admin: { label: "Administrator", color: "#6F7F63", icon: Shield, desc: "Full access to all sections" },
  editor: { label: "Editor", color: "#C7A76C", icon: Settings, desc: "Products, orders, workshops, site config" },
  journal_editor: { label: "Journal Editor", color: "#8A6D63", icon: BookOpen, desc: "Journal management only" },
};

interface EditingUser extends AdminUser {
  password?: string;
}

export function AdminUsers() {
  const { users, setUsers, addUser, updateUser, deleteUser } = useAdminData();
  const { hasPermission, user: currentUser, isSupabaseAuth } = useAdminAuth();
  const [editing, setEditing] = useState<EditingUser | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const sbConnected = isSupabaseConnected();

  // When Supabase is connected, load real admin users (DB uses integer id) so edit/delete use correct ids
  useEffect(() => {
    if (!sbConnected || !hasPermission("users")) return;
    fetchAdminUsers()
      .then(({ data, error }) => {
        if (error) {
          toast.error("Failed to load users");
          return;
        }
        if (Array.isArray(data)) {
          setUsers(
            data.map((row: { id: number; email: string; name: string; role: string; created_at: string; last_login?: string }) => ({
              id: String(row.id),
              email: row.email ?? "",
              name: row.name ?? "",
              role: (row.role ?? "editor") as AdminUser["role"],
              created_at: row.created_at ?? new Date().toISOString(),
              last_login: row.last_login,
            }))
          );
        }
      })
      .catch(() => {
        toast.error("Failed to load users");
      });
  }, [sbConnected, setUsers]);

  if (!hasPermission("users")) {
    return <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>You don't have access to this section.</p>;
  }

  const openNew = () => {
    setIsNew(true);
    setEditing({
      id: `usr_${Date.now()}`,
      email: "",
      name: "",
      role: "editor",
      created_at: new Date().toISOString(),
      password: "",
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    // Basic email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editing.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    // Duplicate email check
    const duplicate = users.find(
      (u) => u.email.toLowerCase() === editing.email.toLowerCase() && u.id !== editing.id
    );
    if (duplicate) {
      toast.error("A user with this email already exists");
      return;
    }
    if (isNew && sbConnected && (!editing.password || editing.password.length < 8)) {
      toast.error("Password must be at least 8 characters for Supabase Auth");
      return;
    }

    setSaving(true);
    try {
      if (sbConnected) {
        if (isNew) {
          const { error } = await createAdminUser({
            email: editing.email,
            name: editing.name,
            role: editing.role,
            password: editing.password,
          });
          if (error) {
            toast.error(`Failed to create user: ${error.message || "Unknown error"}`);
            setSaving(false);
            return;
          }
          // Reload users from Supabase so the new user has the real id for future edit/delete
          const { data: fresh } = await fetchAdminUsers();
          if (fresh?.length) {
            setUsers(
              fresh.map((row: { id: number; email: string; name: string; role: string; created_at: string; last_login?: string }) => ({
                id: String(row.id),
                email: row.email,
                name: row.name ?? "",
                role: row.role as AdminUser["role"],
                created_at: row.created_at ?? new Date().toISOString(),
                last_login: row.last_login,
              }))
            );
          }
        } else {
          const { error } = await updateAdminUserSb(editing.id, {
            name: editing.name,
            role: editing.role,
            email: editing.email,
          });
          if (error) {
            toast.error(`Failed to update user: ${error.message || "Unknown error"}`);
            setSaving(false);
            return;
          }
        }
      }

      // Also update local state (localStorage mirror)
      const { password: _, ...userWithoutPassword } = editing;
      if (isNew) addUser(userWithoutPassword);
      else updateUser(userWithoutPassword);

      toast.success(isNew ? "User created" : "User updated");
      setEditing(null);
    } catch (err: any) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    try {
      if (sbConnected) {
        const { error } = await deleteAdminUserSb(id);
        if (error) {
          toast.error(`Failed to delete user: ${error.message || "Unknown error"}`);
          setSaving(false);
          return;
        }
      }
      deleteUser(id);
      toast.success("User removed");
    } catch (err: any) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setSaving(false);
      setDeleteConfirm(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1
          className="text-[#2D2A26] tracking-[0.08em]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
        >
          User Management
        </h1>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
          style={{ fontSize: "11px" }}
        >
          <Plus size={16} strokeWidth={1.5} />
          Add User
        </button>
      </div>

      {/* Connection status badge */}
      <div className="flex items-center gap-2 mb-6 px-4 py-2.5 rounded-lg border" style={{
        backgroundColor: sbConnected ? "#6F7F63/5" : "#C7A76C08",
        borderColor: sbConnected ? "#6F7F6325" : "#C7A76C25",
      }}>
        {sbConnected ? (
          <Database size={14} className="text-[#6F7F63]" />
        ) : (
          <HardDrive size={14} className="text-[#C7A76C]" />
        )}
        <span style={{ fontSize: "11px", color: sbConnected ? "#6F7F63" : "#C7A76C" }}>
          {sbConnected
            ? "Connected to Supabase — user accounts are managed via Supabase Auth"
            : "Local mode — users are stored in localStorage only. Connect Supabase for real authentication."}
        </span>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-4 mb-6">
        {Object.entries(roleConfig).map(([role, cfg]) => (
          <div key={role} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: cfg.color }} />
            <span className="text-[#6F675F]" style={{ fontSize: "11px" }}>{cfg.label}</span>
          </div>
        ))}
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {users.map((user) => {
          const cfg = roleConfig[user.role] ?? roleConfig.editor;
          const isSelf = user.id === currentUser?.id;
          return (
            <div key={user.id} className="bg-white rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `${cfg.color}15` }}
                  >
                    <span style={{ color: cfg.color, fontSize: "14px" }}>
                      {user.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-[#2D2A26]" style={{ fontSize: "14px" }}>
                      {user.name}
                      {isSelf && <span className="text-[#C7A76C] ml-1" style={{ fontSize: "10px" }}>(you)</span>}
                    </p>
                    <p className="text-[#D8D1C7]" style={{ fontSize: "11px" }}>{user.email}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span
                  className="px-2.5 py-1 rounded-full"
                  style={{ background: `${cfg.color}15`, color: cfg.color, fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase" }}
                >
                  {cfg.label}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setIsNew(false); setEditing({ ...user }); }}
                    className="p-1.5 text-[#6F675F] hover:text-[#6F7F63] transition-colors"
                  >
                    <Pencil size={14} strokeWidth={1.5} />
                  </button>
                  {!isSelf && (
                    <button
                      onClick={() => setDeleteConfirm(user.id)}
                      className="p-1.5 text-[#6F675F] hover:text-[#8B5C58] transition-colors"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
              {user.last_login && (
                <p className="text-[#D8D1C7] mt-3 pt-3 border-t border-[#E6E0D7]" style={{ fontSize: "10px" }}>
                  Last login: {new Date(user.last_login).toLocaleDateString()}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Delete confirm */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={20} className="text-[#8B5C58]" />
              <h3 className="text-[#2D2A26]" style={{ fontSize: "16px" }}>Remove User?</h3>
            </div>
            <p className="text-[#6F675F] mb-2" style={{ fontSize: "13px", fontWeight: 300 }}>
              This user will lose access to the dashboard.
            </p>
            {sbConnected && (
              <p className="text-[#8B5C58] mb-4" style={{ fontSize: "11px", fontWeight: 300 }}>
                This will also remove their Supabase Auth account.
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="flex-1 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-[#F2EEE8] transition-colors disabled:opacity-50"
                style={{ fontSize: "12px" }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={saving}
                className="flex-1 py-2.5 bg-[#8B5C58] text-white rounded-lg hover:bg-[#7A4B48] transition-colors disabled:opacity-50"
                style={{ fontSize: "12px" }}
              >
                {saving ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E0D7]">
              <h2 className="text-[#2D2A26]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
                {isNew ? "Add User" : "Edit User"}
              </h2>
              <button onClick={() => setEditing(null)} className="text-[#D8D1C7] hover:text-[#2D2A26]"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Name *</label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  maxLength={100}
                  className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]"
                  style={{ fontSize: "13px" }}
                />
              </div>
              <div>
                <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Email *</label>
                <input
                  type="email"
                  value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  maxLength={200}
                  className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]"
                  style={{ fontSize: "13px" }}
                />
              </div>

              {/* Password field — shown for new users, or when Supabase is connected */}
              {isNew && (
                <div>
                  <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>
                    {sbConnected ? "Initial Password *" : "Password (optional)"}
                  </label>
                  <input
                    type="password"
                    value={editing.password || ""}
                    onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                    maxLength={128}
                    placeholder={sbConnected ? "Min. 8 characters" : "Not stored locally"}
                    className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C]"
                    style={{ fontSize: "13px" }}
                  />
                  {sbConnected ? (
                    <p className="text-[#6F675F] mt-1.5" style={{ fontSize: "10px", fontWeight: 300 }}>
                      This password will be used for Supabase Auth sign-in. The user can reset it via email.
                    </p>
                  ) : (
                    <p className="text-[#C7A76C] mt-1.5" style={{ fontSize: "10px", fontWeight: 300 }}>
                      Passwords are only functional when Supabase is connected. Currently using mock auth.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Role *</label>
                <div className="space-y-2">
                  {(["admin", "editor", "journal_editor"] as const).map((role) => {
                    const cfg = roleConfig[role];
                    return (
                      <label key={role} className="flex items-center gap-3 p-3 bg-[#F7F5F1] rounded-lg cursor-pointer border border-transparent hover:border-[#E6E0D7] transition-colors">
                        <input
                          type="radio"
                          name="role"
                          value={role}
                          checked={editing.role === role}
                          onChange={() => setEditing({ ...editing, role })}
                          className="w-4 h-4 text-[#6F7F63] focus:ring-[#6F7F63]"
                        />
                        <div>
                          <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>{cfg.label}</p>
                          <p className="text-[#D8D1C7]" style={{ fontSize: "11px" }}>
                            {cfg.desc}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E6E0D7] flex justify-end gap-3">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="px-5 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-[#F2EEE8] transition-colors disabled:opacity-50"
                style={{ fontSize: "12px" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase disabled:opacity-50"
                style={{ fontSize: "12px" }}
              >
                {saving ? "Saving..." : isNew ? "Add User" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
